import axios from 'axios';
import { useAuthenticationStore } from '@/features/auth/stores/authenticationStore.js';
import config from '@/config.js';
import { i18n } from '@/plugins/i18n.js';

export function useClient() {
    const client = axios.create({
        baseURL: config.apiUrl,
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const auth = useAuthenticationStore();

    // Request interceptor
    client.interceptors.request.use(async config => {
        // Check if this is the refresh token endpoint
        const isRefreshEndpoint = config.url?.includes('api/users/refresh');

        // Check if we need to refresh the token
        if (auth.isAuthenticated && !isRefreshEndpoint) {
            try {
                // If token is expiring soon, start a refresh and WAIT for it to complete
                if (auth.isTokenExpiringSoon(auth.accessToken)) {
                    console.log(
                        `Token is expiring soon, waiting for refresh to complete before continuing request: ${config.method?.toUpperCase()} ${config.url}`
                    );
                    await auth.refresh();
                    console.log(
                        `Token refresh completed, proceeding with request: ${config.method?.toUpperCase()} ${config.url}`
                    );
                }
            } catch (error) {
                console.error(`Failed to refresh token for: ${config.method?.toUpperCase()} ${config.url}`, error);
                throw error; // This will cancel the request
            }
        }

        if (auth.isAuthenticated && !isRefreshEndpoint) {
            config.headers.Authorization = `Bearer ${auth.accessToken}`;
        }

        config.headers['Accept-Language'] = i18n.global.locale.value ?? 'en';

        if (config.data instanceof FormData) {
            console.log(
                `Data is FormData, removing explicit Content-Type header for: ${config.method?.toUpperCase()} ${config.url}`
            );
            delete config.headers['Content-Type'];
        }

        return config;
    });

    // Response interceptor
    client.interceptors.response.use(
        response => {
            return response;
        },
        async error => {
            const originalRequest = error.config;
            console.error(
                `Response interceptor caught an error for: ${originalRequest.method?.toUpperCase()} ${originalRequest.url}`,
                error
            );

            // Prevent retry loops by checking if this is already a retry or a refresh request
            if (
                error.response?.status === 401 &&
                !originalRequest._retry &&
                !originalRequest.url.includes('api/users/refresh')
            ) {
                console.log(
                    `Received 401 error for: ${originalRequest.method?.toUpperCase()} ${originalRequest.url}, attempting token refresh...`
                );
                originalRequest._retry = true;

                try {
                    // Use a timeout to prevent hanging indefinitely
                    const refreshTimeout = new Promise((_, reject) => {
                        setTimeout(() => reject(new Error('Token refresh timeout')), 10000); // 10s timeout
                    });

                    // Race the refresh against the timeout
                    await Promise.race([auth.refresh(), refreshTimeout]);

                    console.log(
                        `Token refresh successful, retrying original request: ${originalRequest.method?.toUpperCase()} ${originalRequest.url}`
                    );
                    return client(originalRequest);
                } catch (refreshError) {
                    console.error(
                        `Token refresh failed for: ${originalRequest.method?.toUpperCase()} ${originalRequest.url}, logging out user:`,
                        refreshError
                    );
                    // Let the auth handle the navigation with returnUrl
                    throw refreshError;
                }
            }

            // If it's a refresh request that failed, or a retry that still failed, give up
            if (originalRequest.url.includes('api/users/refresh') || originalRequest._retry) {
                console.log(
                    `Request permanently failed: ${originalRequest.method?.toUpperCase()} ${originalRequest.url}`
                );
            }

            return Promise.reject(error);
        }
    );

    return client;
}
